export interface AbTestVariantConfiguration<TVariant extends string = string> {
    codename: TVariant;
    userPercentage: number;
}

export interface AbTestConfiguration<TTest extends string = string, TVariant extends string = string> {
    testName: TTest;
    variants: readonly AbTestVariantConfiguration<TVariant>[];
}

export interface AbTestSelectedVariant {
    testId: string;
    variantId: string;
}

export interface AbTestStorage {
    read: () => unknown;
    write: (tests: readonly AbTestSelectedVariant[]) => boolean | void;
}

export const abTestsStorageName = "ab-tests";

const fallbackTests = new WeakMap<AbTestStorage, AbTestSelectedVariant[]>();
const storagesWithUnpersistedFallback = new WeakSet<AbTestStorage>();

const isSelectedVariant = (value: unknown): value is AbTestSelectedVariant => {
    if (typeof value !== "object" || value === null) return false;

    const selectedVariant = value as Partial<AbTestSelectedVariant>;
    return typeof selectedVariant.testId === "string" && typeof selectedVariant.variantId === "string";
};

const getFallbackTests = (storage: AbTestStorage): AbTestSelectedVariant[] => [...(fallbackTests.get(storage) ?? [])];

export const getStoredAbTests = (storage: AbTestStorage): AbTestSelectedVariant[] => {
    if (storagesWithUnpersistedFallback.has(storage)) return getFallbackTests(storage);

    try {
        const value = storage.read();
        if (value === null || value === undefined) return getFallbackTests(storage);

        const tests = Array.isArray(value) ? value.filter(isSelectedVariant) : [];
        fallbackTests.set(storage, tests);
        return [...tests];
    } catch {
        return getFallbackTests(storage);
    }
};

export const setStoredAbTests = (storage: AbTestStorage, tests: readonly AbTestSelectedVariant[]): void => {
    fallbackTests.set(storage, [...tests]);

    try {
        if (storage.write(tests) === false) {
            storagesWithUnpersistedFallback.add(storage);
        } else {
            storagesWithUnpersistedFallback.delete(storage);
        }
    } catch {
        storagesWithUnpersistedFallback.add(storage);
    }
};

const selectRandomVariant = <TVariant extends string>(
    variants: readonly AbTestVariantConfiguration<TVariant>[]
): TVariant => {
    const selectableVariants = variants.filter((variant) => variant.userPercentage > 0);
    const totalWeight = selectableVariants.reduce((sum, variant) => sum + variant.userPercentage, 0);

    if (totalWeight <= 0) {
        throw new Error("An A/B test must contain at least one variant with a positive user percentage.");
    }

    const randomWeight = Math.random() * totalWeight;
    let cumulativeWeight = 0;

    for (const variant of selectableVariants) {
        cumulativeWeight += variant.userPercentage;
        if (randomWeight < cumulativeWeight) return variant.codename;
    }

    return selectableVariants[selectableVariants.length - 1].codename;
};

export const getAbTestVariant = <TTest extends string, TVariant extends string>(
    config: AbTestConfiguration<TTest, TVariant>,
    storage: AbTestStorage
): TVariant => {
    const tests = getStoredAbTests(storage);
    const savedTest = tests.find((test) => test.testId === config.testName);

    if (
        savedTest &&
        config.variants.some((variant) => variant.codename === savedTest.variantId && variant.userPercentage > 0)
    ) {
        return savedTest.variantId as TVariant;
    }

    const selectedVariant = selectRandomVariant(config.variants);
    const remainingTests = tests.filter((test) => test.testId !== config.testName);
    setStoredAbTests(storage, [...remainingTests, { testId: config.testName, variantId: selectedVariant }]);

    return selectedVariant;
};
